// Batch G — Prices view.
//
// Side-by-side asking vs user counter-offer per row, with an inline
// "Apply" button that PATCHes `user_counter_offer_price` to the deal.
//
// Persistence flow (per-row state machine):
//
//   idle  ──── edit ─→ dirty ──── apply ─→ saving ──── ok ─→ idle (refreshed)
//                                                  └─ fail → error  ─→ idle
//
// We don't auto-debounce on every keystroke — saving on every digit
// would generate a wave of PATCH + recompute traffic. The user clicks
// Apply when they're done.

import { useState } from "react";
import { Check, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
// Phase 36 Batch Z follow-up — P1 fix: stage editor must be reachable
// from every Portfolio view-tab (Prices/Metrics/Verdicts), not just the
// Basic tab. Mirrors the inline pattern from Portfolio.jsx (lines 857-861).
import StageBadge from "@/components/pipeline/StageBadge";
// Phase 43 Batch BE — canonical helpers. Pre-Phase 43 this file
// inlined its own (incomplete) fallback chain that read
// d.purchase_price as "asking" — post-Batch-AJ that's the EFFECTIVE
// price (= counter when target-offer was applied), so every
// counter-applied row displayed asking==counter==$0 savings while
// the aggregate Hero card showed the real $328K. Phase 42 Finding F.
import { askingFor, counterFor } from "@/lib/dealPriceBasis";

function PriceRow({ d, onSaved }) {
  const fmt = useFmt();
  const initial = counterFor(d);
  const [counter, setCounter] = useState(initial);
  const [saving, setSaving] = useState(false);

  const asking = askingFor(d);
  const counterNum = Number(counter || 0);
  const savingsDollar = asking - counterNum;
  const savingsPercent = asking > 0 ? (savingsDollar / asking) * 100 : 0;
  const dirty = counterNum !== initial;

  const apply = async () => {
    if (counterNum < 0 || counterNum > 999_999_999) {
      toast.error("Counter price out of range");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/deals/${d.id}`, {
        analysis: { counter_offer_price: counterNum },
      });
      toast.success("Counter saved");
      onSaved?.(d.id, counterNum);
    } catch (err) {
      console.error("counter save failed", err);
      toast.error(err?.response?.data?.detail || "Could not save counter");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setCounter(initial);

  return (
    <tr
      data-testid={`prices-row-${d.id}`}
      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/40"
    >
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-slate-900">
            {d.property?.address || "—"}
          </span>
          {d.pipeline_stage ? (
            <StageBadge
              stage={d.pipeline_stage}
              dealId={d.id}
              editable
              size="sm"
            />
          ) : null}
        </div>
        <div className="text-xs text-slate-500">
          {d.property?.city ? `${d.property.city}, ${d.property.state}` : "—"}
        </div>
      </td>
      <td className="px-4 py-3 text-right align-top tabular-nums text-sm text-slate-700">
        {fmt.money(asking, { primaryOnly: true })}
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex items-center justify-end gap-1.5">
          <span className="text-xs text-slate-400">$</span>
          <input
            type="number"
            value={counter}
            min={0}
            step={1000}
            onChange={(e) => setCounter(e.target.value)}
            data-testid={`counter-input-${d.id}`}
            className="w-32 rounded-md border border-slate-200 bg-white px-2 py-1 text-right text-sm tabular-nums shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          {dirty ? (
            <button
              type="button"
              onClick={reset}
              title="Reset"
              data-testid={`counter-reset-${d.id}`}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </td>
      <td
        className={`px-4 py-3 text-right align-top tabular-nums text-sm font-medium ${
          savingsDollar > 0 ? "text-emerald-700" : savingsDollar < 0 ? "text-rose-700" : "text-slate-500"
        }`}
      >
        {fmt.money(savingsDollar, { primaryOnly: true })}
        <div className="text-[11px] font-normal text-slate-400">
          {savingsPercent.toFixed(1)}%
        </div>
      </td>
      <td className="px-4 py-3 text-right align-top">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={apply}
          data-testid={`counter-apply-${d.id}`}
          className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
            !dirty || saving
              ? "cursor-not-allowed bg-slate-100 text-slate-400"
              : "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700"
          }`}
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          Apply
        </button>
      </td>
    </tr>
  );
}

export default function IntelligencePricesTable({ deals, onSaved }) {
  if (!deals || deals.length === 0) return null;
  return (
    <div
      data-testid="prices-view"
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2 text-left font-semibold">Property</th>
            <th className="px-4 py-2 text-right font-semibold">Asking</th>
            <th className="px-4 py-2 text-right font-semibold">Your counter</th>
            <th className="px-4 py-2 text-right font-semibold">Savings</th>
            <th className="px-4 py-2 text-right font-semibold">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => (
            <PriceRow key={d.id} d={d} onSaved={onSaved} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
